import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScreenShareOutlinedIcon from '@mui/icons-material/ScreenShareOutlined';
import { Link } from "react-router-dom";
import { useSessionStore } from './SessionStore';
import { useSiteSettings } from './context/SiteSettingsContext';
import { useUser } from "./context/UserContext";
import AudioRecorderPanel from './components/AudioRecorderPanel';
import DimmedOverlay from './components/DimmedOverlay';
import { useDialog } from './components/Modal/DialogContext';
import ModalWrapper from './components/Modal/ModalWrapper';
import Portal from './components/Portal';
import { getRecordingMaxMinutes } from "./utils/recordingDurationLabel";

interface WakeLockSentinel {
  release: () => Promise<void>;
}

type RecordingState = "inactive" | "recording" | "paused";

type DisplayMediaConstraints = {
  audio?: boolean | MediaTrackConstraints;
  video?: boolean | MediaTrackConstraints;
};

type MediaDevicesWithDisplayMedia = MediaDevices & {
  getDisplayMedia?: (constraints?: DisplayMediaConstraints) => Promise<MediaStream>;
};

type ChromiumDisplayMediaConstraints = DisplayMediaConstraints & {
  selfBrowserSurface?: "include" | "exclude";
  surfaceSwitching?: "include" | "exclude";
  systemAudio?: "include" | "exclude";
};

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    brands?: Array<{ brand: string }>;
  };
};

const DISPLAY_MEDIA_UNSUPPORTED_MESSAGE = "Dieser Browser unterstützt keine Online Aufnahme über die Freigabe im Browser. Für das Audio der Online Sitzung wird Chrome Desktop empfohlen.";
const DISPLAY_MEDIA_FAILED_MESSAGE = "Die Freigabe im Browser konnte nicht gestartet werden. Bitte Chrome Desktop verwenden und den Ton des Tabs freigeben.";
const DISPLAY_MEDIA_ABORTED_MESSAGE = "Die Freigabe im Browser wurde abgebrochen. Bitte starten Sie die Online Aufnahme erneut, wenn Sie den Tab der Online Sitzung freigeben möchten.";
const DISPLAY_MEDIA_DENIED_MESSAGE = "Die Freigabe im Browser wurde nicht erlaubt. Bitte starten Sie erneut und bestätigen Sie die Freigabe im Browserdialog.";
const DISPLAY_MEDIA_NO_AUDIO_MESSAGE = "Die Auswahl enthält keine Audiospur. Bitte Chrome Desktop verwenden und beim Teilen Audio aktivieren. In Firefox und Safari ist Ton aus dem gewählten Tab möglicherweise nicht verfügbar.";
const MICROPHONE_UNSUPPORTED_MESSAGE = "Dieser Browser unterstützt keinen Mikrofonzugriff für die Online Aufnahme.";
const MICROPHONE_FAILED_MESSAGE = "Das Mikrofon konnte nicht gestartet werden. Bitte Mikrofonzugriff erlauben und die Online Aufnahme erneut starten.";
const MICROPHONE_ABORTED_MESSAGE = "Die Mikrofonfreigabe wurde abgebrochen. Bitte starten Sie die Online Aufnahme erneut, wenn Ihr Mikrofon mit aufgenommen werden soll.";
const MICROPHONE_DENIED_MESSAGE = "Die Mikrofonfreigabe wurde nicht erlaubt. Bitte starten Sie erneut und bestätigen Sie den Mikrofonzugriff im Browserdialog.";
const KNOWN_START_ERROR_MESSAGES = new Set([
  DISPLAY_MEDIA_UNSUPPORTED_MESSAGE,
  DISPLAY_MEDIA_NO_AUDIO_MESSAGE,
  MICROPHONE_UNSUPPORTED_MESSAGE,
  MICROPHONE_FAILED_MESSAGE,
  MICROPHONE_ABORTED_MESSAGE,
  MICROPHONE_DENIED_MESSAGE,
]);

const generateAudioTimestamp = (extension: string = ""): string => {
  return `audio_${new Date().toISOString().replace(/[:.]/g, '-')}${extension}`;
};

const CallRecorderTab: React.FC = () => {
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const { fetchSessionDataById, setSelectedSessionId, setIsSessionSelected } = useSessionStore();
  const dialog = useDialog();
  const { user } = useUser();

  const [showTipsDialog, setShowTipsDialog] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState>('inactive');
  const [recordingTime, setRecordingTime] = useState(0);
  const maxMinutes = getRecordingMaxMinutes(user, 60);
  const hasExceededLimit = recordingTime >= maxMinutes * 60;
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);

  const sessionIdRef = useRef<string>('');
  const chunkIdRef = useRef<number>(0);
  const fileNameRef = useRef<string>('');
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStateRef = useRef<RecordingState>('inactive');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const keepAliveRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const volumeBarRef = useRef<HTMLCanvasElement | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const displayStreamRef = useRef<MediaStream | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);

  const stopStreamTracks = (mediaStream: MediaStream | null) => {
    mediaStream?.getTracks().forEach((track) => {
      if (track.readyState !== "ended") {
        track.stop();
      }
    });
  };

  const stopActiveStreams = () => {
    stopStreamTracks(audioStreamRef.current);
    stopStreamTracks(displayStreamRef.current);
    stopStreamTracks(microphoneStreamRef.current);
    audioStreamRef.current = null;
    displayStreamRef.current = null;
    microphoneStreamRef.current = null;
    analyserRef.current = null;
    dataArrayRef.current = null;
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      void audioContextRef.current.close().catch((error) => {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Schließen des AudioContext:", error);
      });
    }
    audioContextRef.current = null;
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      setRecordingState('inactive');

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      stopActiveStreams();
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (recordingStateRef.current === 'recording') {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const sendHeartbeat = async () => {
    try {
      const res = await axios.get('/auth/api/heartbeat/');
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("Heartbeat gesendet:", res.data);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Heartbeat fehlgeschlagen:", error);
    }
  };

  const startKeepAlive = () => {
    if (!keepAliveRef.current) {
      keepAliveRef.current = setInterval(() => {
        fetch("/api/keepalive", { method: "POST" }).catch(() => {});
      }, 30000);
    }
  };

  const stopKeepAlive = () => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  };

  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        // @ts-ignore
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
      }
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("WakeLock Fehler:", error);
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Freigeben des WakeLock:", error);
      }
    }
  };

  const isChromiumBrowser = () => {
    const brands = (navigator as NavigatorWithUserAgentData).userAgentData?.brands ?? [];
    return brands.some((brand) => /Chromium|Google Chrome|Microsoft Edge/i.test(brand.brand));
  };

  const getDisplayMediaConstraints = (): ChromiumDisplayMediaConstraints => {
    const constraints: ChromiumDisplayMediaConstraints = {
      video: true,
      audio: true,
    };

    if (isChromiumBrowser()) {
      constraints.systemAudio = "include";
      constraints.surfaceSwitching = "include";
    }

    return constraints;
  };

  const getDisplayAudioStream = async () => {
    const mediaDevices = navigator.mediaDevices as MediaDevicesWithDisplayMedia | undefined;
    if (!mediaDevices?.getDisplayMedia) {
      throw new Error(DISPLAY_MEDIA_UNSUPPORTED_MESSAGE);
    }

    const displayStream = await mediaDevices.getDisplayMedia(getDisplayMediaConstraints());
    const audioTracks = displayStream.getAudioTracks();

    if (audioTracks.length === 0) {
      stopStreamTracks(displayStream);
      throw new Error(DISPLAY_MEDIA_NO_AUDIO_MESSAGE);
    }

    displayStreamRef.current = displayStream;
    return new MediaStream(audioTracks);
  };

  const getMicrophoneStream = async () => {
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.getUserMedia) {
      throw new Error(MICROPHONE_UNSUPPORTED_MESSAGE);
    }

    try {
      const microphoneStream = await mediaDevices.getUserMedia({ audio: true });
      microphoneStreamRef.current = microphoneStream;
      return microphoneStream;
    } catch (error) {
      stopActiveStreams();

      if (error instanceof DOMException) {
        if (error.name === "AbortError") {
          throw new Error(MICROPHONE_ABORTED_MESSAGE);
        }

        if (error.name === "NotAllowedError") {
          throw new Error(MICROPHONE_DENIED_MESSAGE);
        }
      }

      throw new Error(MICROPHONE_FAILED_MESSAGE);
    }
  };

  const logAudioTracks = (label: string, mediaStream: MediaStream) => {
    if (isSiteSettingsLoading || logLevel < 3) return;

    console.log(label, mediaStream.getAudioTracks().map((track) => ({
      enabled: track.enabled,
      muted: track.muted,
      readyState: track.readyState,
      label: track.label,
    })));
  };

  const createMixedRecordingStream = async () => {
    const displayAudioStream = await getDisplayAudioStream();
    const microphoneStream = await getMicrophoneStream();

    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    const destination = audioContext.createMediaStreamDestination();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    analyserRef.current = analyser;
    dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

    const displaySource = audioContext.createMediaStreamSource(displayAudioStream);
    displaySource.connect(destination);
    displaySource.connect(analyser);

    const microphoneSource = audioContext.createMediaStreamSource(microphoneStream);
    microphoneSource.connect(destination);
    microphoneSource.connect(analyser);

    const mixedStream = destination.stream;
    audioStreamRef.current = mixedStream;

    logAudioTracks("Online Aufnahme Audiospur des ausgewählten Tabs", displayAudioStream);
    logAudioTracks("Online Aufnahme Mikrofon Audiospur", microphoneStream);
    logAudioTracks("Online Aufnahme gemischter Aufnahmestrom", mixedStream);

    return mixedStream;
  };

  const getStartErrorMessage = (error: unknown) => {
    if (error instanceof DOMException) {
      if (error.name === "AbortError") {
        return DISPLAY_MEDIA_ABORTED_MESSAGE;
      }

      if (error.name === "NotAllowedError") {
        return DISPLAY_MEDIA_DENIED_MESSAGE;
      }

      return DISPLAY_MEDIA_FAILED_MESSAGE;
    }

    if (error instanceof Error && error.message) {
      return KNOWN_START_ERROR_MESSAGES.has(error.message)
        ? error.message
        : DISPLAY_MEDIA_FAILED_MESSAGE;
    }

    return "Fehler beim Starten der Online Aufnahme.";
  };

  const switchToMicrophoneRecording = () => {
    document.getElementById('tab-trigger-record')?.click();
  };

  const resetRecordingState = async () => {
    await releaseWakeLock();
    stopKeepAlive();
    stopActiveStreams();
    setMediaRecorder(null);
    setRecordingState('inactive');
    recordingStateRef.current = 'inactive';

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setRecordingTime(0);
    setAudioChunks([]);
  };

  const startRecording = async () => {
    sendHeartbeat();
    setRecordingTime(0);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      await requestWakeLock();
      startKeepAlive();

      sessionIdRef.current = generateAudioTimestamp();
      fileNameRef.current = `${sessionIdRef.current}.wav`;
      chunkIdRef.current = 0;

      const audioStream = await createMixedRecordingStream();
      audioStreamRef.current = audioStream;
      recordingStateRef.current = 'recording';

      const didStart = await startRecordingLoop();
      if (!didStart) {
        await resetRecordingState();
        return;
      }

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Error starting online recording:', error);
      alert(getStartErrorMessage(error));
      await resetRecordingState();
    }
  };

  const startRecordingLoop = async (): Promise<boolean> => {
    try {
      audioChunksRef.current = [];
      if (!audioStreamRef.current) {
        throw new Error('Fehler beim Starten der Online Aufnahme');
      }

      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/wav';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          throw new Error('Dieser Browser unterstützt kein geeignetes Audioformat für die Aufnahme.');
        }
      }

      const recorder = new MediaRecorder(audioStreamRef.current, { mimeType });
      mediaRecorderRef.current = recorder;
      setMediaRecorder(recorder);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        chunkIdRef.current++;
        const isLastChunk = recordingStateRef.current === "inactive";

        sendAudioChunkToServer(blob, fileNameRef.current, sessionIdRef.current, chunkIdRef.current, isLastChunk);

        if (recordingStateRef.current !== "inactive") {
          void startRecordingLoop();
        }
      };

      recorder.start();
      setTimeout(() => {
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
      }, 600000);

      setRecordingState('recording');
      setAudioChunks([]);
      return true;
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Error starting online recording loop:', error);
      alert(error instanceof Error && error.message ? error.message : 'Fehler beim Starten der Online Aufnahme');
      return false;
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      setRecordingState('paused');
      recordingStateRef.current = 'paused';
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      setRecordingState('recording');
      recordingStateRef.current = 'recording';
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = async () => {
    await releaseWakeLock();
    stopKeepAlive();
    if (mediaRecorderRef.current) {
      setRecordingState('inactive');
      recordingStateRef.current = 'inactive';

      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      stopActiveStreams();
      setMediaRecorder(null);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setRecordingTime(0);
      setIsSessionSelected(true);
    }
  };

  const abortRecording = async () => {
    await releaseWakeLock();
    stopKeepAlive();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }

    stopActiveStreams();
    setMediaRecorder(null);
    setRecordingState('inactive');
    recordingStateRef.current = 'inactive';

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setRecordingTime(0);
    setAudioChunks([]);
  };

  const handleAbort = () => {
    dialog({
      type: 'confirm',
      title: 'Online Aufnahme abbrechen',
      message: 'Soll die Online Aufnahme wirklich verworfen werden?',
      confirmButton: 'Bestätigen',
      cancelButton: 'Abbrechen',
      onConfirm: () => abortRecording(),
    });
  };

  const sendAudioChunkToServer = async (
    chunk: Blob,
    filename: string,
    session_id: string,
    chunk_id: number,
    is_lastchunk: boolean
  ) => {
    const formData = new FormData();
    formData.append('audio', chunk, filename);
    formData.append('filename', filename);
    formData.append('session_id', session_id);
    formData.append('chunk_id', chunk_id.toString());
    formData.append('is_lastchunk', is_lastchunk.toString());
    formData.append('recording_type', "recording");
    formData.append('transcript_type', "dialogue");

    try {
      const response = await axios.post(`/api/services/stream-audio/`, formData);
      if (!isSiteSettingsLoading && logLevel >= 3) console.log('Online Audio Chunk erfolgreich gesendet:', response.data);
      if (response.data.session_DB_id) {
        fetchSessionDataById(response.data.session_DB_id);
        setSelectedSessionId(response.data.session_DB_id);
        setIsSessionSelected(true);
      }
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Senden eines Online Audio Chunks:', error);
    }
  };

  const isRecording = recordingState !== 'inactive';

  useEffect(() => {
    const container = document.getElementById('tab-content-call');
    if (!container) return;
    const prevPosition = container.style.position;
    const prevZIndex = container.style.zIndex;
    const prevBg = container.style.backgroundColor;

    if (isRecording) {
      container.style.position = 'relative';
      container.style.zIndex = '200';
      container.style.backgroundColor = '#fff';
    } else {
      container.style.position = prevPosition;
      container.style.zIndex = prevZIndex;
      container.style.backgroundColor = prevBg;
    }

    return () => {
      container.style.position = prevPosition;
      container.style.zIndex = prevZIndex;
      container.style.backgroundColor = prevBg;
    };
  }, [isRecording]);

  return (
    <div className="tw-text-gray-500 tw-flex-1">
      <DimmedOverlay
        active={isRecording}
        targetId="tab-content-call"
        opacity={0.08}
        disableHeader
      />
      <div style={{ display: 'grid', gridTemplateRows: '1fr auto', height: '100%' }}>
        <AudioRecorderPanel
          headerText="Für Online Sitzungen"
          recordingState={recordingState}
          recordingTime={recordingTime}
          onStart={startRecording}
          onPause={pauseRecording}
          onResume={resumeRecording}
          onStop={stopRecording}
          onAbort={handleAbort}
          startLabel="Online Aufnahme starten"
          stopLabel="Online Aufnahme beenden"
          deleteLabel="Löschen"
          durationLabel={
            <span>
              Aufnahmedauer: 2 bis{" "}
              <span className={hasExceededLimit ? "tw-text-yellow-500 tw-font-semibold" : ""}>
                {maxMinutes}
              </span>{" "}
              min
            </span>
          }
          limitWarning={
            hasExceededLimit
              ? `Das Zeitlimit von ${maxMinutes} Minuten ist erreicht. Die Aufnahme läuft weiter.`
              : null
          }
          startIcon={<ScreenShareOutlinedIcon fontSize="small" className="tw-w-5" />}
          startButtonClassName="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal"
          stopButtonClassName="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 _block_19rto_52 recorder__submit-btn !tw-font-normal"
          volumeBarRef={volumeBarRef}
          recordingStateRef={recordingStateRef}
          analyserRef={analyserRef}
          dataArrayRef={dataArrayRef}
          overlayClassName="hidden"
          showHeaderOverlay={false}
          headerClassName="tw-mb-1 tw-gap-1 tw-flex tw-flex-row tw-justify-center z-4"
        />
        <div className="tab-tip tw-mt-1">
          <button
            className="btn-reset tw-underline tw-text-primary-500"
            onClick={() => setShowTipsDialog(true)}
          >
            Kurzanleitung zur Online Aufnahme
          </button>

          {showTipsDialog && (
            <Portal>
              <ModalWrapper onClose={() => setShowTipsDialog(false)} title="Kurzanleitung zur Online Aufnahme">
                <div className="tw-grow tw-flex tw-flex-col">
                  <ul className="sc-gEvEer fteAEG tw-grid tw-gap-4">
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <AssignmentTurnedInIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          Vor Beginn sollten alle Teilnehmenden wissen, dass das Audio der Sitzung mit LumaNote aufgenommen und verarbeitet wird. Holen Sie das ausdrückliche Einverständnis ein.
                          <Link to="/settings/clientconsent" className="sc-eqUAAy iEhnxW tw-text-primary-500 underline"> Einverständnisformular herunterladen</Link>
                        </div>
                      </div>
                    </li>
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <InfoOutlinedIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          Für Online Aufnahmen empfehlen wir Chrome Desktop. Andere Browser können je nach System funktionieren, stellen aber nicht immer den Ton des gewählten Tabs bereit.
                        </div>
                      </div>
                    </li>
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <ScreenShareOutlinedIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          Starten Sie die Online Sitzung möglichst in einem eigenen Browser Tab. Wählen Sie beim Start der Aufnahme genau diesen Tab aus und geben Sie den Ton des Tabs frei.
                        </div>
                      </div>
                    </li>
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <HeadphonesIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          LumaNote benötigt keine Videobilder. Für die Dokumentation wird nur Audio verarbeitet.
                        </div>
                      </div>
                    </li>
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <InfoOutlinedIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          LumaNote nimmt zusätzlich Ihr Mikrofon auf. So werden Ihre Stimme und die Stimme der Gegenseite gemeinsam erfasst.
                        </div>
                      </div>
                    </li>
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <InfoOutlinedIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          Der Browser fragt bei jeder Online Aufnahme erneut nach der Freigabe. Das ist normal und dient dem Schutz der Sitzung.
                        </div>
                      </div>
                    </li>
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <InfoOutlinedIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          Wenn die Online Aufnahme nicht funktioniert, können Sie die normale Mikrofonaufnahme nutzen oder später eine Audiodatei hochladen.
                        </div>
                      </div>
                    </li>
                    <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                          <InfoOutlinedIcon fontSize="small" className="tw-max-w-full" />
                        </div>
                        <div>
                          Beenden Sie die Sitzung über „Online Aufnahme beenden“, damit die Aufnahme verarbeitet wird.
                        </div>
                      </div>
                    </li>
                  </ul>
                  <button
                    type="button"
                    className="btn-reset tw-mt-4 tw-self-start tw-underline tw-text-primary-500"
                    onClick={switchToMicrophoneRecording}
                  >
                    Stattdessen normale Mikrofonaufnahme nutzen
                  </button>
                  <a
                    href="https://lumanote.de/help/online-sitzungen-aufnehmen"
                    target="_blank"
                    rel="noreferrer"
                    className="sc-eqUAAy iEhnxW tw-mt-2 tw-text-primary-500 underline"
                  >
                    Ausführliche Anleitung
                  </a>
                </div>
              </ModalWrapper>
            </Portal>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallRecorderTab;
